import React, { useEffect, useState, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useUserWebSocket } from '../hooks/useUserWebSocket';

interface GlobalWebSocketManagerProps {
  userId: number | null;
}

export const GlobalWebSocketManager: React.FC<GlobalWebSocketManagerProps> = ({ userId }) => {
  const { 
    setConnectionStatus, 
    setIsConnected, 
    setTotalUnreadCount,
    onUnreadCountUpdate,
    onNewMessage
  } = useWebSocket();
  
  // Обработчики для WebSocket сообщений - теперь передаем в Context
  const handleUnreadCountUpdate = useCallback((botName: string, count: number) => {
    // Передаем обновление в Context для синхронизации с UI
    onUnreadCountUpdate(botName, count);
  }, [onUnreadCountUpdate]);
  
  const handleNewMessage = useCallback((message: any) => {
    // Передаем сообщение в Context для синхронизации с UI
    onNewMessage(message);
  }, [onNewMessage]);
  
  const userWs = useUserWebSocket(userId, handleUnreadCountUpdate, handleNewMessage);
  const [subscribedBots, setSubscribedBots] = useState<Set<string>>(new Set());
  const [botMapping, setBotMapping] = useState<{ [key: number]: string }>({});
  
  const isConnected = userWs.isConnected;
  const connectionStatus = userWs.status;

  // Определяем PWA режим (несколько способов)
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true ||
                window.location.protocol === 'https:' && 
                (window.navigator as any).serviceWorker && 
                !window.navigator.userAgent.includes('Chrome') ||
                document.referrer === '' ||
                window.matchMedia('(display-mode: fullscreen)').matches;


  // Принудительное обновление статуса только для PWA режима
  useEffect(() => {
    if (!isPWA) return; // Только для PWA режима

    const interval = setInterval(() => {
      // Проверяем реальный статус WebSocket
      if (userWs.isConnected && connectionStatus !== 'connected') {
        // Принудительно обновляем статус в контексте
        setConnectionStatus('connected');
        setIsConnected(true);
      }
    }, 1000); // Проверяем каждую секунду

    return () => clearInterval(interval);
  }, [isPWA, userWs.isConnected, connectionStatus, setConnectionStatus, setIsConnected]);

  // Дополнительная проверка: если WebSocket подключается, но статус не обновляется
  useEffect(() => {
    if (!isPWA) return; // Только для PWA режима

    const timeout = setTimeout(() => {
      if (userWs.isConnected && connectionStatus !== 'connected') {
        setConnectionStatus('connected');
        setIsConnected(true);
      }
    }, 3000); // Ждем 3 секунды

    return () => clearTimeout(timeout);
  }, [isPWA, userWs.isConnected, connectionStatus, setConnectionStatus, setIsConnected]);

  // Принудительное обновление при изменении isConnected в PWA режиме
  useEffect(() => {
    if (!isPWA) return; // Только для PWA режима
    
    if (userWs.isConnected && connectionStatus !== 'connected') {
      setConnectionStatus('connected');
      setIsConnected(true);
    }
  }, [isPWA, userWs.isConnected, connectionStatus, setConnectionStatus, setIsConnected]);

  // Принудительное обновление UI в PWA режиме
  useEffect(() => {
    if (!isPWA) return; // Только для PWA режима
    
    // Принудительно обновляем состояние каждые 2 секунды в PWA
    const interval = setInterval(() => {
      if (userWs.isConnected && connectionStatus === 'connected') {
        // Принудительно обновляем состояние для перерендера UI
        setConnectionStatus('connected');
        setIsConnected(true);
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [isPWA, userWs.isConnected, connectionStatus, setConnectionStatus, setIsConnected]);

  // Загружаем маппинг ботов
  useEffect(() => {
    const loadBotMapping = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL;
        if (!apiBase) return;
        
        const base = apiBase.replace(/\/$/, '');
        const url = base.endsWith('/api') ? `${base}/bots/mapping` : `${base}/api/bots/mapping`;
        const res = await fetch(url);
        
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.mapping) {
            setBotMapping(data.mapping);
          }
        }
      } catch (e) {
        console.error('Failed to load bot mapping:', e);
      }
    };
    
    loadBotMapping();
  }, []);

  // Подгружаем подписки пользователя и подписываемся через один WS
  useEffect(() => {
    const controller = new AbortController();
    const loadAndSyncSubscriptions = async () => {
      if (!userId || !userWs.isConnected) return;
      try {
        const apiBase = process.env.REACT_APP_API_URL;
        if (!apiBase) return;
        // Если REACT_APP_API_URL уже указывает на "/api", не добавляем ещё раз
        const base = apiBase.replace(/\/$/, '');
        const url = base.endsWith('/api') ? `${base}/user/subscriptions` : `${base}/api/user/subscriptions`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
          signal: controller.signal
        });
        if (!res.ok) return;
        const data = await res.json();
        // Фильтруем активные подписки по датам (вместо поля status)
        const today = new Date().toISOString().split('T')[0]; // "2025-08-26"
        
        const active = (data.subscriptions || []).filter((s: any) => {
          return s.subscription_start <= today && today <= s.subscription_until;
        });
        
        // Получаем имена ботов по bot_id, используя загруженный маппинг
        const activeBotNames: string[] = [];
        for (const sub of active) {
          if (sub.bot_name) {
            activeBotNames.push(sub.bot_name);
          } else if (sub.bot_id && botMapping[sub.bot_id]) {
            // Используем маппинг, загруженный из API
            const botName = botMapping[sub.bot_id];
            activeBotNames.push(botName);
          } else if (sub.bot_id) {
            // Bot ID не найден в маппинге
          }
        }

        // Подписываемся на новые
        const nextSet = new Set(subscribedBots);
        for (const bot of activeBotNames) {
          if (!nextSet.has(bot)) {
            userWs.subscribe(bot);
            nextSet.add(bot);
          }
        }
        // Отписываемся от неактуальных
        for (const bot of Array.from(nextSet)) {
          if (!activeBotNames.includes(bot)) {
            userWs.unsubscribe(bot);
            nextSet.delete(bot);
          }
        }
        setSubscribedBots(nextSet);
      } catch (e) {
        // игнорируем тихо, статус соединения отобразится отдельно
      }
    };
    loadAndSyncSubscriptions();
    return () => controller.abort();
  }, [userId, userWs.isConnected, botMapping]);

  // Синхронизируем состояние с глобальным контекстом
  useEffect(() => {
    setConnectionStatus(connectionStatus);
    setIsConnected(isConnected);
  }, [connectionStatus, isConnected, setConnectionStatus, setIsConnected]);

  // Загружаем начальные счетчики непрочитанных, когда есть соединение
  useEffect(() => {
    const controller = new AbortController();
    const loadUnread = async () => {
      if (!userId || !isConnected) return;
      try {
        const apiBase = process.env.REACT_APP_API_URL;
        if (!apiBase) return;
        const base = apiBase.replace(/\/$/, '');
        const url = base.endsWith('/api') ? `${base}/chat/unread_counts` : `${base}/api/chat/unread_counts`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
          signal: controller.signal
        });
        if (!res.ok) return;
        const data = await res.json();
        const counts = data.unread_counts || {};
        const total = Object.values(counts).reduce((sum: number, v: any) => sum + (typeof v === 'number' ? v : 0), 0);
        setTotalUnreadCount(total);
      } catch (e) {
        // ignore
      }
    };
    loadUnread();
    return () => controller.abort();
  }, [userId, isConnected, setTotalUnreadCount]);

  // Этот компонент не рендерит ничего, только управляет WebSocket
  return null;
};
